import React, { useState, useEffect } from "react";
import GalleryForm from "../../components/gallery/GalleryForm";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { GalleryServices } from "../../services/admin/GalleryServices";
import { useNotification } from "../../context/NotificationContext";

export default function EditGalleryItemPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showNotification } = useNotification();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        async function fetchItem() {
            try {
                const found = await GalleryServices.getItemById(id);
                if (found) {
                    setItem(found);
                } else {
                    navigate("/admin/gallery");
                }
            } catch (err) {
                console.error("Failed to load item", err);
                navigate("/admin/gallery");
            } finally {
                setLoading(false);
            }
        }
        fetchItem();
    }, [id, navigate]);

    const handleUpdate = async (data) => {
        try {
            // Build FormData when a file or youtube_link is present (or media type changed)
            if (data.file || data.youtube_link || data.type !== item.media_type) {
                const fd = new FormData();
                fd.append("title", data.title || "");
                fd.append("media_type", data.type || item.media_type);
                if (data.youtube_link) fd.append("youtube_link", data.youtube_link);
                if (data.file) fd.append("file", data.file);
                fd.append("downloadable", data.downloadable);

                const result = await GalleryServices.updateItem(id, fd);
                if (result.status === "success") {
                    showNotification({
                        type: "success",
                        title: "Updated",
                        message: result.item?.video_status === "processing"
                            ? "Media replaced. Video is processing in the background."
                            : "Media details updated."
                    });
                    navigate("/admin/gallery");
                }
            } else {
                // JSON-only update (title and downloadable)
                const payload = {
                    title: data.title,
                    downloadable: data.downloadable
                };
                const result = await GalleryServices.updateItem(id, payload);
                if (result.status === "success") {
                    showNotification({
                        type: "success",
                        title: "Updated",
                        message: "Media details updated."
                    });
                    navigate("/admin/gallery");
                }
            }
        } catch (error) {
            console.error("Update failed", error);
            showNotification({
                type: "failed",
                title: "Error",
                message: error.response?.data?.message || "Failed to update."
            });
        }
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <Loader2 className="animate-spin text-jazz-brown" />
            </div>
        );
    }

    return (
        <>
            <div className="p-6 lg:p-10 max-w-5xl mx-auto">
                {/* Navigation Header */}
                <button
                    onClick={() => navigate("/admin/gallery")}
                    className="flex items-center gap-2 text-black dark:text-white/40 hover:text-black/70 dark:hover:text-white transition-colors text-xs font-bold tracking-widest uppercase mb-6"
                >
                    <ChevronLeft size={16} /> BACK TO GALLERY
                </button>

                <div className="mb-10">
                    <h1 className="text-3xl lg:text-4xl font-normal text-black dark:text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                        Edit Media Details
                    </h1>
                    <p className="text-black dark:text-white/50 text-sm italic font-serif">
                        Update the caption or replace the media file.
                    </p>
                </div>

                {/* Form */}
                <GalleryForm initialData={item} onSubmit={handleUpdate} isEditing={true} />
            </div>
        </>
    );
}
